import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Download } from "lucide-react";
import { apiFetch } from "@/api/client";
import {
  useMailStatus,
  useMessage,
  useMessages,
  useSuggestions,
  type MessageStatus,
} from "@/api/mail";
import { MessageRow } from "@/components/mail/MessageRow";
import { SuggestionCard } from "@/components/mail/SuggestionCard";
import { RequestError } from "@/components/RequestError";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { SelectField, TextField } from "@/components/ui/field";
import { PageHeader } from "@/components/ui/page-header";
import { TabPanel, Tabs, pickTab, type TabItem } from "@/components/ui/tabs";
import { saveBlob } from "@/lib/files";

const TABS = ["decide", "recent"] as const;
type MailTab = (typeof TABS)[number];
const TAB_ITEMS: TabItem<MailTab>[] = [
  { id: "decide", label: "To decide" },
  { id: "recent", label: "Recent mail" },
];
const STATUS_OPTIONS = [
  { value: "new", label: "Not filed" },
  { value: "filed", label: "Filed" },
  { value: "ignored", label: "Ignored" },
];
/** A poll that has not run for this long means the mail service is down. */
const STALE_AFTER_MS = 60 * 60 * 1000;

function SyncBanner() {
  const status = useMailStatus();
  if (!status.data) return null;
  if (!status.data.configured) {
    return (
      <Alert title="Mail intake is not set up">
        Set PBO_IMAP_HOST, PBO_IMAP_USERNAME and PBO_IMAP_PASSWORD (a Google app password) on the
        server and start the mail service. See deploy/README.md.
      </Alert>
    );
  }
  const last = status.data.last_polled_at ? new Date(status.data.last_polled_at).getTime() : 0;
  const stale = Date.now() - last > STALE_AFTER_MS;
  if (status.data.last_error || stale) {
    return (
      <Alert
        variant="warning"
        title={stale ? "The mailbox has not synced for over an hour" : "The last poll hit an error"}
      >
        {status.data.last_error ||
          `Last synced ${status.data.last_polled_at ? new Date(status.data.last_polled_at).toLocaleString() : "never"}.`}
      </Alert>
    );
  }
  return null;
}

function MessageDetail({ id, onClose }: { id: string; onClose: () => void }) {
  const message = useMessage(id);
  const [busy, setBusy] = useState(false);
  const [downloadError, setDownloadError] = useState<unknown>(null);
  async function download(attachmentId: string, filename: string): Promise<void> {
    setBusy(true);
    setDownloadError(null);
    try {
      const res = await apiFetch(`/mail/attachments/${attachmentId}`);
      await saveBlob(await res.blob(), filename);
    } catch (err) {
      setDownloadError(err);
    } finally {
      setBusy(false);
    }
  }
  if (message.isPending) return <p className="text-muted-foreground">Loading…</p>;
  if (message.isError) return <RequestError error={message.error} />;
  const m = message.data;
  return (
    <Card className="border-primary/40">
      <CardHeader>
        <CardTitle className="text-base">{m.subject || "(no subject)"}</CardTitle>
        <p className="text-xs text-muted-foreground">
          From {m.from_name ? `${m.from_name} <${m.from_addr}>` : m.from_addr} ·{" "}
          {new Date(m.sent_at).toLocaleString()}
          {m.to_addrs.length > 0 && ` · to ${m.to_addrs.join(", ")}`}
          {m.cc_addrs.length > 0 && ` · cc ${m.cc_addrs.join(", ")}`}
        </p>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <pre className="max-h-96 overflow-auto whitespace-pre-wrap rounded bg-muted/40 p-3 text-sm">
          {m.text_body}
        </pre>
        {m.attachments.length > 0 && (
          <ul className="flex flex-wrap gap-2">
            {m.attachments.map((a) => (
              <li key={a.id}>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!a.stored || busy}
                  onClick={() => void download(a.id, a.filename)}
                  title={a.stored ? undefined : "Too large to keep"}
                >
                  <Download className="h-3.5 w-3.5" aria-hidden /> {a.filename}
                </Button>
              </li>
            ))}
          </ul>
        )}
        <RequestError error={downloadError} />
        {m.suggestions.length > 0 && (
          <p className="text-xs text-muted-foreground">
            Suggestions: {m.suggestions.map((s) => `${s.kind} (${s.status})`).join(", ")}
          </p>
        )}
        <div>
          <Button size="sm" variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function DecidePanel() {
  const suggestions = useSuggestions("pending");
  if (suggestions.isPending) return <p className="text-muted-foreground">Loading…</p>;
  if (suggestions.isError) return <RequestError error={suggestions.error} />;
  if (suggestions.data.length === 0) {
    return (
      <EmptyState
        title="Nothing to decide"
        description="New e-mails that need a call, an event or a vessel will appear here."
      />
    );
  }
  return (
    <ul className="flex flex-col gap-3">
      {suggestions.data.map((s) => (
        <li key={s.id}>
          <SuggestionCard suggestion={s} />
        </li>
      ))}
    </ul>
  );
}

function RecentPanel({ onOpen }: { onOpen: (id: string) => void }) {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const messages = useMessages({
    q: q || undefined,
    status: (status || undefined) as MessageStatus | undefined,
  });
  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_12rem]">
        <TextField
          id="mail-q"
          label="Search"
          value={q}
          onChange={setQ}
          hint="Subject, sender or text"
        />
        <SelectField
          id="mail-status"
          label="Status"
          value={status}
          onChange={setStatus}
          placeholder="All"
          options={STATUS_OPTIONS}
        />
      </div>
      {messages.isPending ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : messages.isError ? (
        <RequestError error={messages.error} />
      ) : messages.data.length === 0 ? (
        <EmptyState
          title="No mail here"
          description="The poller stores every e-mail the company mailbox receives."
        />
      ) : (
        <ul className="flex flex-col gap-2">
          {messages.data.map((m) => (
            <MessageRow key={m.id} message={m} onOpen={() => onOpen(m.id)} />
          ))}
        </ul>
      )}
    </div>
  );
}

export function MailPage() {
  const [params, setParams] = useSearchParams();
  const tab = pickTab(params.get("tab"), TABS, "decide");
  const open = params.get("message");
  function setOpen(id: string | null): void {
    const next: Record<string, string> = {};
    if (tab !== "decide") next.tab = tab;
    if (id) next.message = id;
    setParams(next, { replace: true });
  }
  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Mail"
        description="What the company mailbox received, filed under each vessel call, and what the app thinks it means."
      />
      <SyncBanner />
      {open && <MessageDetail id={open} onClose={() => setOpen(null)} />}
      <div>
        <Tabs
          label="Mail"
          items={TAB_ITEMS}
          value={tab}
          onChange={(next) => setParams(next === "decide" ? {} : { tab: next }, { replace: true })}
        />
        <TabPanel id={tab}>
          {tab === "decide" && <DecidePanel />}
          {tab === "recent" && <RecentPanel onOpen={(id) => setOpen(id)} />}
        </TabPanel>
      </div>
    </div>
  );
}
